// ============================================================================
// InboxIndicator - the navbar's tray icon and count.
//
// Mounted by Navbar itself, not the per-page `actions` slot, so it is present
// for every account type on every page. The count is what still waits on you:
// unanswered invites plus shares you have not saved or dismissed. Peak Orange,
// because it asks for attention, and hidden at zero, because a badge that is
// always lit teaches people to ignore it.
// ============================================================================

import { useId } from 'react'
import { Box } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useInboxCount } from '@/services/api/inbox'
import { beoflowColors, borderRadius, typography } from '@/theme/muiTheme'

export function InboxIndicator() {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  // A stable accessible name. The count changes, and an aria-label that did
  // with it broke selectors and replays; the count moves to a described-by
  // element instead.
  const descriptionId = useId()
  // Only ask when there is a session: the navbar also renders on the auth and
  // share pages, where the endpoint would 401.
  const { data } = useInboxCount({ enabled: isAuthenticated })
  if (!isAuthenticated) return null

  const count = data?.count ?? 0
  const badge = count > 9 ? '9+' : String(count)

  return (
    <Box
      component="button"
      type="button"
      aria-label="Inbox"
      aria-describedby={count > 0 ? descriptionId : undefined}
      onClick={() => navigate('/inbox')}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        p: 0,
        cursor: 'pointer',
        borderRadius: borderRadius.round,
        backgroundColor: 'rgba(248, 246, 244, 0.06)',
        border: '1px solid rgba(248, 246, 244, 0.1)',
        color: 'text.primary',
        transition: 'background-color 0.2s ease',
        '&:hover': { backgroundColor: 'rgba(248, 246, 244, 0.12)' },
      }}
    >
      {/* The tray glyph from the design frame, inlined so it tints with the bar. */}
      <Box
        component="svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        sx={{ width: 22, height: 22 }}
      >
        <path d="M3 13h5l1.5 3h5L16 13h5" />
        <path d="M5.5 5h13L21 13v6H3v-6z" />
      </Box>
      {count > 0 && (
        <Box
          component="span"
          data-testid="inbox-count"
          sx={{
            position: 'absolute',
            top: 1,
            right: 1,
            minWidth: 16,
            height: 16,
            px: '4px',
            borderRadius: '9999px',
            backgroundColor: beoflowColors.peakOrange,
            color: beoflowColors.beoBlack,
            fontFamily: typography.fontFamilyMonospace,
            fontSize: '10px',
            fontWeight: typography.fontWeightBold,
            lineHeight: '16px',
            textAlign: 'center',
          }}
        >
          {badge}
        </Box>
      )}
      {count > 0 && (
        <Box
          component="span"
          id={descriptionId}
          sx={{
            position: 'absolute', width: 1, height: 1, p: 0, m: -1,
            overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
          }}
        >
          {`${count} waiting`}
        </Box>
      )}
    </Box>
  )
}
